/* eslint-disable complexity */
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { Forecast } from '@futrli-sdk/predict';
import { ForecastUploadDrawer } from '@futrli/shared-services/excel-upload/excel-modals/upload/forecast-upload-drawer';
import { UpdateFromExcelModalContent } from '@futrli/shared-services/excel-upload';
import { useExcelFilesView } from '@futrli/views/hooks/useExcelFilesView';

import { CreateFromExcelModalContent } from './create-from-excel-modal-content';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';

interface Props {
  handleCloseExcelUpdate: () => void;
  isOpenExcelUpdate: boolean;
}

export const ForecastExcelUpload = ({ handleCloseExcelUpdate, isOpenExcelUpdate }: Props) => {
  const { forecast, isConsolidation } = usePredictionsInfoContext();
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();

  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';

  if (!forecast || isConsolidation || !isOpenExcelUpdate) {
    return null;
  }

  return (
    <ForecastExcelUploadContent
      forecast={forecast as Forecast}
      workspaceId={workspaceId}
      organisationId={organisationId}
      handleCloseExcelUpdate={handleCloseExcelUpdate}
      isOpenExcelUpdate={isOpenExcelUpdate}
    />
  );
};

interface ContentProps {
  forecast: Forecast;
  workspaceId: string;
  organisationId: string;
  handleCloseExcelUpdate: () => void;
  isOpenExcelUpdate: boolean;
}

const ForecastExcelUploadContent = ({
  forecast,
  workspaceId,
  organisationId,
  handleCloseExcelUpdate,
  isOpenExcelUpdate
}: ContentProps) => {
  const { viewInstance, files, isFetchingFiles, isGeneratingTemplate, templateUrl } = useExcelFilesView(
    workspaceId,
    organisationId,
    { entityType: 'forecast', forecastId: forecast.id }
  );

  useEffect(() => {
    if (!isFetchingFiles && !files.length && !templateUrl) {
      viewInstance.generateForecastTemplate({
        forecast_kind: forecast.kind as 'budget' | 'scenario',
        forecast_from_date: forecast.forecast_from_date || ''
      });
    }
  }, [viewInstance, isFetchingFiles, files.length, templateUrl, forecast.kind, forecast.forecast_from_date]);

  const isLoading = isFetchingFiles || isGeneratingTemplate;
  const isUpdatingExcelForecast = files.length > 0;

  return (
    <ForecastUploadDrawer
      title={
        isLoading ? (
          ''
        ) : isUpdatingExcelForecast ? (
          <FormattedMessage id="predict.update_excel_predictions" defaultMessage="Update Excel predictions" />
        ) : forecast.kind === 'budget' ? (
          <FormattedMessage
            id="predict.excel_upload_budget"
            defaultMessage="Upload a 1-year P&L budget, forecast or scenario from Excel"
          />
        ) : (
          <FormattedMessage
            id="predict.excel_upload_scenario"
            defaultMessage="Upload a 3-year cash flow budget, forecast or scenario from Excel"
          />
        )
      }
      isOpen={isOpenExcelUpdate}
      handleClose={handleCloseExcelUpdate}
      workspaceId={workspaceId}
      organisationId={organisationId}
      isLoading={isLoading}
      isUpdateDrawer={isUpdatingExcelForecast}
      isPredictionsPage={true}
      forecastData={{
        forecast_from_date: forecast.forecast_from_date || '',
        forecast_name: forecast.name,
        forecast_kind: forecast.kind as 'budget' | 'scenario',
        forecast_id: forecast.id,
        include_unpaid_invoices: forecast.include_unpaid_invoices,
        categories_with_ai_enabled: null
      }}
    >
      {isUpdatingExcelForecast ? (
        <UpdateFromExcelModalContent
          workspaceId={workspaceId}
          organisationId={organisationId}
          config={{ entityType: 'forecast', forecastId: forecast.id }}
          guidelinesUrl="https://support.futrli.com/en/articles/11641325-how-to-update-a-forecast-with-an-excel-template"
        />
      ) : templateUrl ? (
        <CreateFromExcelModalContent
          forecastType={forecast.kind as 'budget' | 'scenario'}
          forecastTemplateLink={templateUrl}
        />
      ) : (
        <div />
      )}
    </ForecastUploadDrawer>
  );
};
